/**
 * Product Layering - Dynamic Opus Variant Switching
 * Updates recommendations when customer selects different Opus variants
 */

(function () {
  "use strict";

  class ProductLayering {
    constructor() {
      // Find any product-layering section (all categories)
      this.section = document.querySelector(".product-layering");
      if (!this.section) return;

      this.productId = this.section.dataset.productId;
      this.category = this.section.dataset.category;
      this.container = this.section.querySelector(".product-layering__container");
      this.heading = this.section.querySelector(".product-layering__heading");
      this.grid = this.section.querySelector(".product-layering__grid");

      this.init();
      this.loadFallbackRecommendations();
    }

    init() {
      // Listen for variant changes on the product form
      const variantSelects = document.querySelectorAll(
        '[name="id"], input[name="options[Scent]"], select[name="options[Scent]"]',
      );

      variantSelects.forEach((select) => {
        select.addEventListener("change", () => this.handleVariantChange());
      });

      // Also listen for custom variant change events
      document.addEventListener("variant:change", (e) => {
        if (e.detail && e.detail.variant) {
          this.updateRecommendations(e.detail.variant);
        }
      });
    }

    handleVariantChange() {
      // Get the selected variant
      const variantIdSelect = document.querySelector('[name="id"]');
      if (!variantIdSelect) return;

      const selectedVariantId = variantIdSelect.value;

      // Find the variant data from the product JSON
      const productData = window.productData || this.getProductDataFromDOM();
      if (!productData) return;

      const selectedVariant = productData.variants.find((v) => v.id == selectedVariantId);
      if (selectedVariant) {
        this.updateRecommendations(selectedVariant);
      }
    }

    updateRecommendations(variant) {
      // Extract Opus value from variant (usually in option1 or options with name "Scent")
      let opusValue = null;

      if (variant.option1 && variant.option1 !== "Default Title") {
        opusValue = variant.option1;
      } else if (variant.options && variant.options.length > 0) {
        opusValue = variant.options[0];
      }

      if (!opusValue || opusValue === "Default Title") return;

      // Update heading
      if (this.heading) {
        this.heading.textContent = "More in Opus";
      }

      // Update data attribute
      this.section.dataset.opus = opusValue;

      // Reload recommendations via AJAX
      this.loadRecommendations(opusValue);
    }

    async loadRecommendations(opus) {
      if (!this.grid) return;

      // Show loading state
      this.grid.classList.add("loading");
      this.grid.style.opacity = "0.5";

      try {
        // Fetch updated section HTML
        const url = `${window.location.pathname}?section_id=product-layering&opus=${encodeURIComponent(opus)}`;
        const response = await fetch(url);
        const html = await response.text();

        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newGrid = doc.querySelector(".product-layering__grid");

        if (newGrid && newGrid.innerHTML.trim()) {
          // Update the grid with new products
          this.grid.innerHTML = newGrid.innerHTML;

          // Dispatch event for any other scripts that need to reinitialize
          document.dispatchEvent(
            new CustomEvent("product-layering:updated", {
              detail: { opus },
            }),
          );
        }
      } catch (error) {
        console.error("Error loading recommendations:", error);
      } finally {
        // Remove loading state
        this.grid.classList.remove("loading");
        this.grid.style.opacity = "1";
      }
    }

    getProductDataFromDOM() {
      // Try to find product data from script tag
      const productScript = document.querySelector(
        "script[data-product-json], script#product-json",
      );
      if (productScript) {
        try {
          return JSON.parse(productScript.textContent);
        } catch (e) {
          console.error("Error parsing product JSON:", e);
        }
      }

      // Fallback: construct from variant select options
      const variantSelect = document.querySelector('[name="id"]');
      if (!variantSelect) return null;

      const variants = Array.from(variantSelect.options).map((option) => ({
        id: option.value,
        title: option.textContent,
        option1: option.textContent.split(" - ")[0] || option.textContent,
      }));

      return { variants };
    }

    /**
     * Load fallback recommendations using Shopify Product Recommendations API
     */
    async loadFallbackRecommendations() {
      // Only load for fallback sections using product-recommendations element
      const productRecommendations = document.querySelector("product-recommendations");
      if (!productRecommendations) return;

      const url = productRecommendations.dataset.url;
      if (!url) return;

      try {
        // Fetch recommendations section HTML
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch recommendations");

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newRecommendations = doc.querySelector("product-recommendations");

        if (newRecommendations) {
          // Find the container wrapper inside the fetched content
          const newContainer = doc.querySelector(".product-layering__container");

          if (newContainer) {
            // Find existing container wrapper
            let existingContainer = productRecommendations.querySelector(
              ".product-layering__container",
            );

            if (existingContainer) {
              // Replace container content, preserving the wrapper structure
              existingContainer.innerHTML = newContainer.innerHTML;
            } else {
              // Fallback: replace entire content (maintains backward compatibility)
              productRecommendations.innerHTML = newRecommendations.innerHTML;
            }
          } else {
            // No container found, replace entire content
            productRecommendations.innerHTML = newRecommendations.innerHTML;
          }

          // Dispatch event for any other scripts
          document.dispatchEvent(new CustomEvent("product-cards:loaded"));
        } else {
          // No recommendations available - hide the entire section
          const parentSection = productRecommendations.closest(".product-layering");
          if (parentSection) {
            parentSection.style.display = "none";
          }
        }
      } catch (error) {
        console.error("Error loading recommendations:", error);
        // Hide the entire section if loading fails
        const parentSection = productRecommendations.closest(".product-layering");
        if (parentSection) {
          parentSection.style.display = "none";
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new ProductLayering());
  } else {
    new ProductLayering();
  }
})();
